/**
 * Storefront ACL – Utilidades genéricas de envelope y conversión.
 *
 * Manejo centralizado del envelope { data, meta } que devuelve el backend
 * y conversión de decimales a centavos para el shape legacy.
 */

import type { ApiEnvelope } from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// Envelope unwrapping
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Desenvuelve una respuesta paginada del backend.
 * Soporta tanto `{ data: T[], meta }` como un array directo.
 */
export function unwrapList<T>(input: unknown): {
  items: T[];
  meta: Record<string, unknown>;
} {
  const payload = input as ApiEnvelope<T[]>;
  return {
    items: Array.isArray(payload?.data) ? payload.data : [],
    meta: (payload?.meta as Record<string, unknown>) ?? {},
  };
}

/**
 * Desenvuelve una respuesta singular del backend.
 * Soporta tanto `{ data: T }` como un objeto directo.
 */
export function unwrapData<T>(input: unknown): T {
  const payload = input as ApiEnvelope<T> | T;
  return ((payload as ApiEnvelope<T>)?.data ?? payload) as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// Conversión monetaria
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convierte un decimal (string o number) a centavos enteros.
 * El backend envía salePrice como string decimal ("125.50").
 * El shape legacy (ProductDTO) espera centavos enteros (12550).
 */
export function decimalToCents(
  value: string | number | null | undefined,
): number {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}
